import React from 'react';
import { 
  User, Save, Loader2, CreditCard, Clock, KeyRound,
  Eye, EyeOff
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const VendorUserTab = ({
  user,
  stall,
  accountData,
  setAccountData,
  savingAccount,
  handleSaveAccount,
  subscription,
  handleStallStatusChange,
  passwordData,
  setPasswordData,
  showPassword,
  setShowPassword,
  changingPassword,
  handleChangePassword,
  marketLocations
}) => {
  // Check if all required stall fields are filled
  const checkRequiredFields = () => {
    if (!stall) return { complete: false, missing: ['Stall data not loaded'] };
    
    const missing = [];
    
    // Basic info
    if (!stall.stallName?.trim()) missing.push('Company/Stall Name');
    if (!stall.category?.trim()) missing.push('Category');
    if (!stall.slogan?.trim()) missing.push('Slogan');
    if (!stall.about?.trim()) missing.push('About');
    
    // Contact info
    if (!stall.phone?.trim()) missing.push('Phone');
    if (!stall.email?.trim()) missing.push('Email');
    
    // Images
    if (!stall.logo?.trim()) missing.push('Logo');
    if (!stall.banner?.trim()) missing.push('Banner');
    
    // Markets & Locations - check if at least one market with complete location info
    const locations = stall.locations || [];
    if (locations.length === 0) {
      missing.push('At least one market');
    } else {
      // Check if all selected markets have required location data
      const incompleteMarkets = locations.filter(loc => 
        !loc.stallNumber?.trim() || !loc.lat || !loc.lng
      );
      if (incompleteMarkets.length > 0) {
        missing.push('Complete location info for all markets (stall number & GPS coordinates)');
      }
    }
    
    return { 
      complete: missing.length === 0, 
      missing 
    };
  };

  const stallComplete = checkRequiredFields();
  
  // Determine if vendor can publish based on subscription AND stall completeness
  const canPublishSubscription = subscription.plan === 'cash' || subscription.plan === 'manual' || 
    (subscription.plan === 'trial' && subscription.status === 'active') ||
    ((subscription.plan === 'monthly' || subscription.plan === 'yearly') && 
     (subscription.status === 'active' || subscription.status === 'pending_cancellation'));
  
  const canPublish = canPublishSubscription && stallComplete.complete;

  return (
    <div className="space-y-6 mt-4">
      {/* Account Status Section */}
      <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
        <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
          <User className="w-5 h-5 text-[#FFD12D]" />
          Account
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-[#252525] dark:text-white">Account Status</Label>
            <div className="px-3 py-2 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-semibold text-sm">
              ✓ Approved
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[#252525] dark:text-white">Stall Visibility</Label>
            <Select
              value={stall?.status || 'draft'}
              onValueChange={(value) => handleStallStatusChange(value)}
              disabled={!canPublish && stall?.status !== 'published'}
            >
              <SelectTrigger className={`dark:bg-[#1a1a1a] dark:text-white ${!canPublish && stall?.status !== 'published' ? 'opacity-60 cursor-not-allowed' : ''}`}>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                    Draft
                  </span>
                </SelectItem>
                <SelectItem value="published" disabled={!canPublish}>
                  <span className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${canPublish ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                    Published {!canPublish && '(Requirements not met)'}
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {stall?.status === 'published' 
                ? 'Your stall is visible to customers' 
                : 'Your stall is hidden from customers'}
            </p>
            {/* Show missing fields warning */}
            {!stallComplete.complete && stall?.status !== 'published' && (
              <div className="text-xs text-orange-600 dark:text-orange-400 mt-2 p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <p className="font-medium mb-1">Complete your stall to publish:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {stallComplete.missing.slice(0, 5).map((field, idx) => (
                    <li key={idx}>{field}</li>
                  ))}
                  {stallComplete.missing.length > 5 && (
                    <li>...and {stallComplete.missing.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}
            {/* Subscription warning - only show if stall is complete but subscription is the issue */}
            {stallComplete.complete && !canPublishSubscription && stall?.status !== 'published' && subscription.status !== 'pending_cancellation' && (
              <p className="text-xs text-orange-600 dark:text-orange-400 mt-1 flex items-center gap-1">
                <CreditCard className="w-3 h-3" />
                {subscription.plan === 'trial' && subscription.status !== 'active'
                  ? 'Your 7-day trial has expired. Subscribe to publish your stall.'
                  : 'Please subscribe to publish your stall.'}
              </p>
            )}
            {subscription.status === 'pending_cancellation' && (
              <p className="text-xs text-orange-600 dark:text-orange-400 mt-1 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Your subscription is cancelling. Stall will remain active until {subscription.nextBillingDate ? new Date(subscription.nextBillingDate).toLocaleDateString('en-ZA') : 'billing period ends'}.
              </p>
            )}
            {subscription.plan === 'trial' && subscription.status === 'active' && stallComplete.complete && (
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                You're on a 7-day free trial. Subscribe before it ends to keep your stall published.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Account Information */}
      <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
        <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
          <User className="w-5 h-5 text-[#FFD12D]" />
          Account Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-[#252525] dark:text-white">Name <span className="text-red-500">*</span></Label>
            <Input
              value={accountData.firstName}
              onChange={(e) => setAccountData({...accountData, firstName: e.target.value})}
              placeholder="Happy"
              className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[#252525] dark:text-white">Surname <span className="text-red-500">*</span></Label>
            <Input
              value={accountData.lastName}
              onChange={(e) => setAccountData({...accountData, lastName: e.target.value})}
              placeholder="User"
              className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[#252525] dark:text-white">Company Name <span className="text-red-500">*</span></Label>
            <Input
              value={accountData.companyName}
              onChange={(e) => setAccountData({...accountData, companyName: e.target.value})}
              placeholder="Best Stall"
              className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[#252525] dark:text-white">Email <span className="text-red-500">*</span></Label>
            <Input
              value={user?.email || ''}
              placeholder="hello@wayo.co.za"
              disabled
              className="dark:bg-[#1a1a1a] dark:text-white bg-gray-100 dark:bg-gray-800 placeholder:text-gray-400 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">Email cannot be changed</p>
          </div>
          <div className="space-y-2">
            <Label className="text-[#252525] dark:text-white">Phone <span className="text-red-500">*</span></Label>
            <Input
              value={accountData.phone}
              onChange={(e) => setAccountData({...accountData, phone: e.target.value})}
              placeholder="+27-74-809-4378"
              className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
            />
          </div>
        </div>
        
        <div className="flex justify-end pt-4">
          <Button
            onClick={handleSaveAccount}
            disabled={savingAccount}
            className="bg-[#FFD12D] hover:bg-[#e6bc28] text-[#252525] font-semibold"
          >
            {savingAccount ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Account Info
              </>
            )}
          </Button>
        </div>
      </div>

      <Separator className="bg-gray-200 dark:bg-gray-700" />

      {/* Change Password Section */}
      <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
        <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-[#FFD12D]" />
          Change Password
        </h3>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[#252525] dark:text-white">Current Password</Label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={passwordData.current}
                onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                placeholder="Enter current password"
                className="dark:bg-[#1a1a1a] dark:text-white pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[#252525] dark:text-white">New Password</Label>
            <Input
              type={showPassword ? 'text' : 'password'}
              value={passwordData.new}
              onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
              placeholder="Enter new password"
              className="dark:bg-[#1a1a1a] dark:text-white"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[#252525] dark:text-white">Confirm New Password</Label>
            <Input
              type={showPassword ? 'text' : 'password'}
              value={passwordData.confirm}
              onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
              placeholder="Confirm new password"
              className="dark:bg-[#1a1a1a] dark:text-white"
            />
          </div>
          <Button
            onClick={handleChangePassword}
            disabled={changingPassword || !passwordData.current || !passwordData.new || !passwordData.confirm}
            className="bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D]"
          >
            {changingPassword ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Changing...
              </>
            ) : (
              <>
                <KeyRound className="w-4 h-4 mr-2" />
                Change Password
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VendorUserTab;
